import React from "react";
import { Select, ButtonGroup, NumberInput } from "@caelor/ui";
import { usePagesStore } from "../../../../../../stores/use-page-store";
import {
  BOOLEAN_OPTIONS,
  CORNERS_OPTIONS,
  PagesVariants,
  PageElements,
} from "./defaults";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import { PaginationTypes } from "@Shared/Pagination-Types";
import { useTranslation } from "react-i18next";

function LayoutSettings() {
  const { t } = useTranslation();

  const { data, selectedTab, updateConfig, updateConfigTab } = usePagesStore(
    (state) => ({
      data: state.data,
      selectedTab: state.getSelectedTab(),
      updateConfig: state.updateConfig,
      updateConfigTab: state.updateConfigTab,
    })
  );

  let displayElements = [];

  if (data.config.variant === PagesVariants.MINI_CARD) {
    displayElements = [
      { label: t("space"), value: PageElements.SPACE },
      { label: t("author"), value: PageElements.AUTHOR },
    ];
  } else {
    displayElements = [
      { label: t("space"), value: PageElements.SPACE },
      { label: t("author"), value: PageElements.AUTHOR },
      { label: t("date"), value: PageElements.DATE },
      { label: t("likes"), value: PageElements.LIKES },
      { label: t("comments"), value: PageElements.COMMENTS },
    ];
  }

  const options =
    data.config.variant === PagesVariants.CARD
      ? [
          ...displayElements,
          { label: t("description"), value: PageElements.DESCRIPTION },
        ]
      : displayElements;

  const PaginationOptions = [
    { label: t("none"), value: PaginationTypes.NONE },
    { label: t("show-more"), value: PaginationTypes.INFINITE },
    { label: t("previous-or-next"), value: PaginationTypes.CAROUSEL },
  ];

  return (
    <>
      {data.config?.tabs[selectedTab]?.enrichment === "dynamic" && (
        <SettingsOptionContainer>
          <SettingsOptionHeading>{t("display-options")}</SettingsOptionHeading>

          <OptionContainer>
            <OptionLabel>{t("pagination")}</OptionLabel>
            <OptionContent>
              <Select
                options={PaginationOptions}
                value={PaginationOptions.find(
                  ({ value }) => value === data.config.pagination
                )}
                onChange={({ value }) =>
                  updateConfig({ value: { pagination: value } })
                }
              />
            </OptionContent>
          </OptionContainer>

          {data.config.variant !== PagesVariants.MAGAZINE && (
            <OptionContainer>
              <OptionLabel>{t("per-page")}</OptionLabel>
              <OptionContent>
                <NumberInput
                  value={data.config.tabs[selectedTab].limit}
                  setValue={(value) =>
                    updateConfigTab({ value: { limit: value } })
                  }
                  min={1}
                  isFullWidth
                />
              </OptionContent>
            </OptionContainer>
          )}
        </SettingsOptionContainer>
      )}

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("link")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("new-tab")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={BOOLEAN_OPTIONS}
              value={data?.config?.openInNewTab ?? true}
              onClick={(isOn) =>
                updateConfig({
                  value: { openInNewTab: isOn },
                })
              }
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>
          {t("flexible-display-items")}
        </SettingsOptionHeading>
        {options.map((option) => (
          <OptionContainer key={option.value}>
            <OptionLabel>{option.label}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={data.config.activeElements.includes(option.value)}
                onClick={(isOn) => {
                  const newElements = isOn
                    ? [...data.config.activeElements, option.value]
                    : data.config.activeElements.filter(
                        (el) => el !== option.value
                      );
                  updateConfig({
                    value: { activeElements: newElements },
                  });
                }}
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        ))}
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("corners")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              i18n={t}
              items={CORNERS_OPTIONS}
              value={data.config.cardBorderRadius}
              options={{ input: true }}
              onClick={(value) =>
                updateConfig({
                  value: { cardBorderRadius: value },
                })
              }
              onChange={({ target: { value } }) => {
                if (value.length < 4) {
                  updateConfig({
                    value: { cardBorderRadius: value },
                  });
                }
              }}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
    </>
  );
}

export default LayoutSettings;
